import { Pressable, Text } from "react-native";
import type { StyleProp, TextStyle, ViewStyle } from "react-native";
import type { LucideIcon } from "lucide-react-native";
import { semanticColors, typography } from "../theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type TextActionButtonTone =
  "secondary" | "tertiary" | "destructive" | "onDark";
export type TextActionButtonSize = "md" | "sm" | "xs";

const TONE_COLOR: Record<TextActionButtonTone, string> = {
  secondary: semanticColors.text.secondary,
  tertiary: semanticColors.text.tertiary,
  destructive: semanticColors.text.destructive,
  /** On the dark auth and hero screens. */
  onDark: semanticColors.onDark.text.primary,
};

const ICON_SIZE: Record<TextActionButtonSize, number> = {
  md: 16,
  sm: 13,
  xs: 12,
};

/**
 * `md` reads like the label of the SecondaryButton it usually sits beside;
 * `sm` sits beside a CompactActionButton.
 */
const SIZE_STYLE: Record<
  TextActionButtonSize,
  { fontSize: number; lineHeight: number; fontWeight: TextStyle["fontWeight"] }
> = {
  md: { fontSize: 15, lineHeight: 20, fontWeight: "500" },
  sm: { fontSize: 12.5, lineHeight: 16, fontWeight: "700" },
  /** Inside a row, beside its 11 px status labels. */
  xs: { fontSize: 11, lineHeight: 14, fontWeight: "700" },
};

/**
 * A button with no surface, for the quieter alternative that sits beside or
 * under the main buttons (Clear all, Remove provider). Actions inside a row
 * use an icon button instead.
 */
export type TextActionButtonProps = {
  label: string;
  onPress: () => void;
  accessibilityLabel?: string;
  /** Stable analytics identifier, required when `label` is translated. */
  action?: string;
  tone?: TextActionButtonTone;
  size?: TextActionButtonSize;
  disabled?: boolean;
  /** Centers it under the buttons above, and its label if it wraps. */
  centered?: boolean;
  /** Icon drawn in the label's colour. Retries pass RotateCcw. */
  icon?: LucideIcon;
  /** `trailing` puts the icon after the label, as an arrow onward. */
  iconPosition?: "leading" | "trailing";
  testID?: string;
  style?: StyleProp<ViewStyle>;
};

export function TextActionButton({
  label,
  onPress,
  action,
  accessibilityLabel,
  tone = "secondary",
  size = "md",
  disabled = false,
  centered = false,
  icon: Icon,
  iconPosition = "leading",
  testID,
  style,
}: TextActionButtonProps) {
  const capturePress = usePressCapture("TextActionButton");
  const iconNode = Icon ? (
    <Icon size={ICON_SIZE[size]} color={TONE_COLOR[tone]} strokeWidth={2} />
  ) : null;
  return (
    <Pressable
      onPress={
        disabled
          ? undefined
          : () => {
              capturePress(pressLabel(label, accessibilityLabel), action);
              onPress();
            }
      }
      accessibilityRole="button"
      disabled={disabled}
      testID={testID}
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityState={{ disabled }}
      style={({ pressed }) => [
        {
          paddingVertical: size === "md" ? 12 : 6,
          paddingHorizontal: size === "md" ? 16 : 8,
          opacity: disabled ? 0.44 : pressed ? 0.66 : 1,
        },
        Icon && {
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 5,
        },
        centered && { alignSelf: "center" },
        style,
      ]}
    >
      {Icon && iconPosition === "leading" ? iconNode : null}
      <Text
        className="font-dm-sans"
        style={{
          ...SIZE_STYLE[size],
          fontFamily: typography.families.sans,
          color: TONE_COLOR[tone],
          ...(centered ? { textAlign: "center" as const } : null),
        }}
      >
        {label}
      </Text>
      {Icon && iconPosition === "trailing" ? iconNode : null}
    </Pressable>
  );
}

export default TextActionButton;
